import * as Yup from 'yup';
export interface TranslationMap {
  [key: string]: string;
}

export interface CounselingStage {
  id:number
  name: string;
  code: string;
  translation: TranslationMap | null;
}
export interface CounselingType {
  id:number
  name: string;
  code: string;
  translation: TranslationMap | null;
}
export interface PatientResponse {
  id:number
  name: string;
  code: string;
  translation: TranslationMap | null;
}
export interface ConcessionReason {
  id:number
  name: string;
  code: string;
  translation: string | null;
}
export interface CancelReason {
  id:number
  name: string;
  code: string;
  translation: string | null;
}
export interface PaymentMethod {
  id:number
  name: string;
  code: string;
  translation: string | null;
}
export interface UpiProvider {
  id:number
  name: string;
  code: string;
  translation: string | null;
}
export interface OnlinePaymentMode {
  id:number
  name: string;
  code: string;
  translation: string | null;
}
export interface Category {
  id:number
  name: string;
  code: string;
  translation: string | null;
}
export interface CodeType {
  id:number
  name: string;
  code: string;
  translation: string | null;
}
export interface SacCode {
  id:number
  name: string;
  code: string;
  translation: string | null;
}
export interface ClassName {
  id:number
  name: string;
  code: string;
  translation: string | null;
}
export interface DurationType {
  id:number
  name: string;
  code: string;
  translation: string | null;
}
export interface DistributionType {
  id:number
  name: string;
  code: string;
  translation: string | null;
}
export interface ServiceSourceType {
  id:number
  name: string;
  code: string;
  translation: string | null;
}

export interface TariffGroup {
  id:number
  name: string;
  code: string;
}


export const validationSchema = Yup.object({
  package_concession_amount: Yup.number().min(0),
  package_definition: Yup.object({
    duration_type: Yup.string().required('Required'),
    distribution_type: Yup.string().required('Required'),
    package_name_snapshot: Yup.string(),
    package_amount_snapshot: Yup.number(),
    package_duration: Yup.number().required('Required').min(1),
    pharmacy_amount: Yup.number().required('Required').min(0),
    package_amount: Yup.number().required('Required').min(0),
    service_amount: Yup.number().required('Required').min(0),
    pharmacy_percent: Yup.number().min(0, 'Pharmacy percentage cannot be less than 0%').max(100, 'Pharmacy percentage cannot exceed 100%'),
    total_service_amount: Yup.number()
      .min(0)
      .test('total-equals-service', 'Total service amount must equal Service Amount', function (value) {
        const serviceAmount = this.parent?.service_amount;
        if (value == null || serviceAmount == null) return true;
        return Number(value) === Number(serviceAmount);
      }),
    service_percent: Yup.number().min(0, 'Service percentage cannot be less than 0%').max(100, 'Service percentage cannot exceed 100%'),
    package_net_amount: Yup.number().min(0),
    remark: Yup.string(),
    is_finalized: Yup.boolean(),
    procedure_details: Yup.array().of(
      Yup.object({
        id: Yup.number(),
        procedure_id: Yup.number().required('Procedure ID is required'),
      })
    ).min(1, 'At least one cycle is required'),
  }).test(
    'amounts-match',
    'Service Amount + Pharmacy Amount must equal Package Amount',
    function (value) {
      const serviceAmount = Number(value?.service_amount) || 0;
      const pharmacyAmount = Number(value?.pharmacy_amount) || 0;
      const packageAmount = Number(value?.package_amount) || 0;
      // Allow small floating point tolerance
      return Math.abs((serviceAmount + pharmacyAmount) - packageAmount) < 0.01;
    }
  ),
  service_items: Yup.array().of(
    Yup.object({
      service: Yup.number().min(0),
      // Keep placeholder rows valid (service=0), but enforce required fields once a service is selected.
      service_name: Yup.string().when('service', {
        is: (service: number) => Number(service) > 0,
        then: schema => schema.required('Required'),
        otherwise: schema => schema.notRequired(),
      }),
      service_source_type: Yup.string(),
      procedure: Yup.string().nullable(),
      category: Yup.string(),
      gender: Yup.string().when('service', {
        is: (service: number) => Number(service) > 0,
        then: schema => schema.required('Required'),
        otherwise: schema => schema.notRequired(),
      }),
      adjustable_head: Yup.boolean(),
      adjustable_head_type: Yup.string().when('adjustable_head', {
        is: true,
        then: schema => schema.oneOf(['clinical', 'pharmacy']).required('Required'),
        otherwise: schema => schema.notRequired(),
      }),
      is_consumables: Yup.boolean(),
      discount_percent: Yup.number().min(0).max(100),
      discount_amount: Yup.number().min(0),
      discount_on_quantity: Yup.boolean(),
      unlimited_quantity: Yup.boolean(),
      quantity: Yup.number().min(0),
      unit_rate: Yup.number().min(0),
      unit_percentage: Yup.number().min(0).max(100),
      total_amount: Yup.number().min(0),
    })
  ),
  pharmacy_items: Yup.array().of(
    Yup.object({
      item: Yup.number().notRequired(),
      item_name: Yup.string().notRequired(),
      quantity: Yup.number().notRequired().nullable(),
      uom: Yup.string().notRequired(),
      discount_percent: Yup.number().min(0).max(100),
      total_amount: Yup.number().min(0),
    })
  ).nullable(),
});
export interface PharmacyItem {
  id?: number;
  item: number;
  item_name?: string;
  uom?: number | null;
  uom_description?: string | null;
  quantity: number;
  discount_percent: string | number;
  total_amount?: string | number;
  uom_dropdown?:UomConversion[] | null;
  uom_conversion?:UomConversion[] | null;
}


// Interface for procedure details in multi-select
export interface ProcedureDetail {
  id?: number;  // Exists for existing items (from GET), used for PUT updates
  procedure_id: number;
  procedure_source_type?: string;
  procedure_source_name?: string;
}

export interface ServiceItem {
  unlimited_qty?: boolean;
  id?: number;
  service: number;
  service_name: string;
  service_source_type: string;
  procedure: string;
  category: string;
  gender: string;
  adjustable_head: boolean;
  adjustable_head_type?: string | null;
  is_consumables: boolean;
  discount_percent: string | number;
  discount_on_quantity: boolean;
  quantity: number;
  unit_rate: string | number;
  unit_percentage?: string | number;
  total_amount: string | number;
  discount_amount?: string | number;
  original_unit_rate?: string | number;
  original_quantity?: number;
  // Track which procedures this service came from (for multi-select support)
  procedure_source_ids?: number[];
}

// Interface for package tariff definition item returned from API
export interface PackageTariffDefinitionItem {
  package_tariff_id: number;
  tariff_name: string;
  tariff_code: string;
  package_definition: {
    id: number;
    package_duration: number;
    package_name: string;
    package_amount: string;
    duration_type: string;
    distribution_type: string;
    service_amount: string;
    service_pct: string;
    pharmacy_amount: string;
    pharmacy_pct: string;
    total_service_amount: string;
    package_concession_amount: string;
    package_net_amount: string;
    procedure_source_type: string;
    procedure_source_id: number | null;
    adjustable_head: boolean;
    is_consumables: boolean;
    consumable_amount: string;
    is_finalized: boolean;
  };
  service_items: ServiceItem[];
  pharmacy_items: PharmacyItem[];
}

export interface TariffCounts {
  total_tariff: number;
  tariff_with_definition: number;
  tariff_without_definition: number;
}

export interface packageDetailsForTariffResponse {
  count: number;
  definitions?: PackageTariffDefinitionItem[];
  tariff_counts?: TariffCounts;
}
export interface PackageDefinitionFormValues {
  id?: number;
  package_tariff_id?: number;
  tariff_name?: string;
  tariff_code?: string;
  package_code?: string;
  package_concession_amount?: string | number;
  package_definition: {
    id?: number;
    package?: number;
    package_name?: string;
    package_amount?: number;
    package_duration: number;
    duration_type: string;
    distribution_type: string | null;
    service_amount: string | number;
    service_pct?: string | number;
    pharmacy_amount: string | number;
    pharmacy_pct?: string | number;
    total_service_amount?: string | number;
    package_gross_amount?: string | number;
    package_concession_amount?: string | number;
    package_net_amount?: string | number;
    procedure_source_type?: string | null;
    procedure_source_name?: string | null;
    procedure_source_id?: number | null;
    // Support for multiple procedures via procedure_details array
    procedure_details?: ProcedureDetail[];
    remark: string;
    is_active?: boolean;
    is_finalized: boolean;
    created_on?: string;
    updated_on?: string;
    created_by?: number;
    updated_by?: number;
    // Extra fields in interface but not in payload are optional
    package_name_snapshot?: string;
    package_amount_snapshot?: number;
    pharmacy_percent?: number;
    service_percent?: number;
    adjustable_head?: boolean;
    is_consumables?: boolean;
    consumable_amount?: number;
    pharmacy_set_all_items?: boolean;
  };
  service_items: ServiceItem[];
  pharmacy_items: PharmacyItem[] | null;
}

export const initialValues: PackageDefinitionFormValues = {
  package_concession_amount: 0,
  package_definition: {
    package_name: '',
    package_amount: 0,
    duration_type: '',
    distribution_type: null,
    package_duration: 0,
    pharmacy_amount: 0,
    pharmacy_percent: 0,
    total_service_amount:0,
    service_amount: 0,
    service_percent: 0,
    adjustable_head: false,
    is_consumables: false,
    consumable_amount: 0,
    pharmacy_set_all_items: false,
    remark: '',
    is_finalized: false,
    procedure_details: [],
  },
  service_items: [
    {
      service: 0,
      service_name: '',
      service_source_type: '',
      procedure: '',
      category: '',
      gender: '',
      adjustable_head: false,
      adjustable_head_type: '',
      is_consumables: false,
      discount_percent: 0,
      discount_amount: 0,
      discount_on_quantity: false,
      unlimited_qty: false,
      quantity: 1,
      unit_percentage: 0,
      unit_rate: 0,

      total_amount: 0,
    },
  ],
  pharmacy_items: [
    {
      item: 0,
      item_name: '',
      quantity: 1,
      uom: null,
      discount_percent: 0,
      uom_dropdown:null,
    },
  ],
};



export interface PackageLevel {
  id: number;
  level_code: string;
  level_name: string;
}

export interface PackageClassRate {
  id: number;
  class_name: string;
  rate: string;
  code: string;
}

export interface PackageRule {
  id: number;
  rule_code: string;
  rule_name: string;
  rule_description: string;
  active: boolean;
  min_percent: string | null;
  max_percent: string | null;
  percentage: string | null;
  amount: string | null;
  min_amount: string | null;
  max_amount: string | null;
}

export interface PackageDetails {
  id: number;
  code: string;
  code_type: string;
  code_details: string;
  sac_code: string;
  package_name: string;
  short_description: string;
  long_description: string;
  package_amount: string;
  effective_date: string;
  expiry_date: string;
  status: boolean;
  freeze: boolean;
  levels: PackageLevel[];
  class_rates: PackageClassRate[];
  rules: PackageRule[];
}
export interface meta{
  limit: number;
  next: string | null;
  offset: number;
  previous: string | null;
  total_count: number;
 }
export interface PackageListResponse {
  meta: meta;
  objects: PackageDetails[]; // Add this line for the actual package data
}


export interface PharmacyBatch {
  id: number;
  batch_number: string;
  expiry_date: string;
}

export interface UomConversion {
  id: number | null;
  from_uom: number;
  from_uom_label:string
  conversion_factor: number;
}

export interface PharmacyItemDetails {
  id: number;
  code: string;
  name: string;
  brand_name: string;
  cost_price: string;
  mrp: string;
  is_active: boolean;
  is_perishable: boolean;
  inclusive_of_tax: boolean;
  is_free: boolean;
  is_stimulation_drug: boolean;
  is_trigger_drug: boolean;
  manufacturing_company: number;
  marketed_by: string;
  min_expiry_in_days: number;
  storage_temperature: string | null;
  strength: string | null;
  category: string;
  group: string;
  molecule: string;
  molecule_label: string;
  stock_uom: string;
  stock_uom_label: string | null;
  dispensing_type: string;
  pregnancy_class: string | null;
  route: string;
  storage_type: string | null;
  strength_type: string | null;
  therapeutic_class: string | null;
  batches: PharmacyBatch[];
  taxes: unknown[]; // Adjust type if you have tax details
  uom_conversion: UomConversion[];
  cost_price_with_tax: number;
  mrp_with_tax: number;
  name_label: string;
  brand_name_label: string;
}

export interface PharmacyListResponse {
  meta: meta;
  objects: PharmacyItemDetails[];
}


// TypeScript interfaces for the provided configuration structure

export interface BillingSource {
  name: string;
  code: string;
  translation: TranslationMap | null;
}
export interface ItemGroup {
  name: string;
  code: string;
}

export interface ItemCategory {
  name: string;
  code: string;
}

export interface UOM {
  id: number;
  name: string;
  code: string;
}
export interface PackageProcedureMaster {
  name: string;
  code: string;
  translation: TranslationMap;
}
export interface billingConfigResponse {
  COUNSELING_STAGE: CounselingStage[];
  COUNSELING_TYPE: CounselingType[];
  PATIENT_RESPONSE: PatientResponse[];
  CONCESSION_REASON: ConcessionReason[];
  CANCEL_REASON: CancelReason[];
  PAYMENT_METHOD: PaymentMethod[];
  UPI_PROVIDER: UpiProvider[];
  ONLINE_PAYMENT_MODE: OnlinePaymentMode[];
  CATEGORY: Category[];
  CODE_TYPE: CodeType[];
  SAC_CODE: SacCode[];
  CLASS_NAME: ClassName[];
  BILLING_SOURCE: BillingSource[];
  DURATION_TYPE: DurationType[];
  DISTRIBUTION_TYPE: DistributionType[];
  service_source_type: ServiceSourceType[];
  item_group: ItemGroup[];
  item_category: ItemCategory[];
  uom: UOM[];
  tariff_group: TariffGroup[];
  PACKAGE_PROCEDURE_MASTER: PackageProcedureMaster[];
  TAX_TYPE:PatientResponse[];

}

export interface ServiceMasterItem {
  id: number;
  category: string;
  code: string;
  name: string;
  charges: string;
  source: string;
  service_type: string;
  procedure: string;
}


export interface ProcedureItem {
  id: number;
  description: string;
  icon_path: string;
  gender: string;
  procedure_type: string;
  name: string;
  for_patient_type: string;
  overview_links: string;
  pages: string;
  name_label: string;
  description_label: string;
  type: string;
}

export interface ProcedureListResponse {
  success: boolean;
  items: ProcedureItem[];
  count: number;
}

export interface CycleProcedureItem {
  id: number;
  description: string;
  icon_path: string;
  gender: string;
  procedure_type: string;
  name: string;
  for_patient_type: string;
  overview_links: string;
  pages: string;
  name_label: string;
  description_label: string;
}

export interface CycleProcedureListResponse {
  success: boolean;
  category: string;
  items: CycleProcedureItem[];
  count: number;
}

export interface SubCategoryItem {
  id: number;
  description: string;
  icon_path: string;
  gender: string;
  procedure_type: string;
  name: string;
  for_patient_type: string;
  overview_links: string;
  pages: string;
  name_label: string;
  description_label: string;
  type?: string; // present for some categories
}

export interface SubCategoryResponse {
  success: boolean;
  category?: string;
  items: SubCategoryItem[];
  count: number;
}

export interface LabTestCategoryItem {
  id: number;
  name: string;
  icon_url: string | null;
  gender: string;
  test_ids: number[];
  label: string;
}

export interface LabTestCategoryListResponse {
  success: boolean;
  category: string;
  items: LabTestCategoryItem[];
  count: number;
}

export interface ProcedureServiceItem {
  service_id: number;
  service_name: string;
  service_code: string;
  service_source_type: string;
  procedure: number;
  adjustable_head: boolean;
  is_consumables: boolean;
  discount_on_qty: number;
  unlimited_qty: boolean;
  discount_percent: number;
  discount_amount: number;
  quantity: number;
  unit_rate: number;
  unit_percentage: number;
  total_amount: number;
}

export interface ProcedureServicesResponse {
  success: boolean;
  procedure_id: number;
  procedure_name: string;
  services: ProcedureServiceItem[];
  count: number;
}

export function getPackageListURL(): string {
  return 'packages/';
};

export function getPharmacyListURL(): string {
  return 'item/?is_drug=true';
}; 

export function getbillingConfigURL(): string {
  return 'billing/billing-config-options/';
};

export function getServicesURL(): string {
  return 'billing/search-services/';
};
export function PackageDetailsURL(): string {
  return 'patient-packages/';
};

export function getPackageDetailsURL(): string {
  return 'patient-packages/counseling';
};

export function putPackageDetailsURL(): string {
  return 'patient-packages/';
};

export function packageDefinitionURL(packageId: number , tariffId?: number):string{
  return `packages/${packageId}/definition/${tariffId ? `${tariffId}/` : ''}`;
}
export function getPackageDetails(packageId: number, patientProcedureId: number):string{
  return `patient-packages/load_package_services/?package=${packageId}&patient_procedure=${patientProcedureId}`;
}
export function getProcedureLists(): string {
  return 'packages/procedures-list/';
};

export function getSubCateList(category: string): string {
  return `packages/sub-category-list/?category=${category}`;
};

export function getDefaultServicesList(procedureId?: number, type?: string, tariffId?: number): string {
  const params = new URLSearchParams();
  if (procedureId) params.set('procedure_id', String(procedureId));
  if (type) params.set('type', type);
  if (tariffId) params.set('tariff_id', String(tariffId));
  const query = params.toString();
  return `packages/procedure-services/${query ? `?${query}` : ''}`;
};